import { useEffect, useMemo, useState, type FormEvent } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import Modal, { inputCls, labelCls } from '@/components/base/Modal';
import {
  fetchDiscounts,
  saveDiscount,
  deleteDiscount,
  toggleDiscount,
  isDiscountLive,
  discountLineLabel,
  DISCOUNT_LINE_OPTIONS,
  type Discount,
  type DiscountInput,
} from '@/services/discounts';
import { useProducts } from '@/hooks/useProducts';

function toLocalInput(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const emptyDiscount = (): DiscountInput => ({
  name: '',
  discountPercent: 10,
  startDate: '',
  endDate: '',
  isGlobal: true,
  productIds: [],
  lineType: 'both',
  isActive: true,
});

export default function DiscountsPage() {
  const { data: products } = useProducts();
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [editing, setEditing] = useState<DiscountInput | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [productSearch, setProductSearch] = useState('');

  async function load() {
    setLoading(true);
    setError('');
    try {
      setDiscounts(await fetchDiscounts());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar las promociones.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const activeProducts = useMemo(() => products.filter((p) => p.active), [products]);
  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    if (!q) return activeProducts;
    return activeProducts.filter(
      (p) => p.name.toLowerCase().includes(q) || p.sku.toLowerCase().includes(q),
    );
  }, [activeProducts, productSearch]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!editing) return;
    setSaving(true);
    setFormError('');
    const { error: err } = await saveDiscount(editing);
    setSaving(false);
    if (err) {
      setFormError(err);
      return;
    }
    setEditing(null);
    setFeedback('Promoción guardada correctamente.');
    await load();
  }

  async function handleToggle(d: Discount) {
    const { error: err } = await toggleDiscount(d.id, !d.isActive);
    if (err) {
      setError(err);
      return;
    }
    await load();
  }

  async function handleDelete(id: string) {
    const { error: err } = await deleteDiscount(id);
    if (err) {
      setError(err);
      return;
    }
    setFeedback('Promoción eliminada.');
    await load();
  }

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1000px] mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Descuentos y Promociones</h1>
            <p className="text-sm text-foreground-500 mt-0.5">
              Se aplican automáticamente en la tienda y en la venta directa según la fecha y las referencias.
            </p>
          </div>
          <button
            onClick={() => {
              setEditing(emptyDiscount());
              setFormError('');
              setProductSearch('');
            }}
            className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
          >
            <i className="ri-add-line mr-1.5"></i>Nueva promoción
          </button>
        </div>

        {feedback && (
          <div className="flex items-center gap-2 text-sm bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-lg px-4 py-2.5">
            <i className="ri-check-line"></i>{feedback}
          </div>
        )}
        {error && (
          <div className="flex items-center gap-3 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-4 py-2.5">
            <i className="ri-error-warning-line"></i>{error}
            <button onClick={load} className="ml-auto text-xs font-medium underline whitespace-nowrap cursor-pointer">Reintentar</button>
          </div>
        )}

        {loading ? (
          <div className="text-center py-16 text-foreground-400">
            <i className="ri-loader-4-line animate-spin text-2xl mr-2"></i>Cargando...
          </div>
        ) : discounts.length === 0 ? (
          <div className="bg-white rounded-xl border border-background-200 text-center py-16 text-foreground-500">
            <i className="ri-percent-line text-4xl block mb-3"></i>
            <p className="text-sm">Aún no hay promociones. Crea la primera.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-100 text-left bg-background-50">
                    <th className="px-5 py-3 text-xs font-medium text-foreground-500">Promoción</th>
                    <th className="px-5 py-3 text-xs font-medium text-foreground-500">Descuento</th>
                    <th className="px-5 py-3 text-xs font-medium text-foreground-500 hidden md:table-cell">Vigencia</th>
                    <th className="px-5 py-3 text-xs font-medium text-foreground-500 hidden sm:table-cell">Alcance</th>
                    <th className="px-5 py-3 text-xs font-medium text-foreground-500">Estado</th>
                    <th className="px-5 py-3 text-xs font-medium text-foreground-500 w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {discounts.map((d) => {
                    const live = isDiscountLive(d);
                    return (
                      <tr key={d.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                        <td className="px-5 py-3.5">
                          <p className="font-medium text-foreground-900">{d.name}</p>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="text-accent-600 font-semibold">-{d.discountPercent}%</span>
                        </td>
                        <td className="px-5 py-3.5 hidden md:table-cell text-xs text-foreground-600 whitespace-nowrap">
                          {d.startDate ? new Date(d.startDate).toLocaleDateString('es-CO') : '—'}
                          {' → '}
                          {d.endDate ? new Date(d.endDate).toLocaleDateString('es-CO') : 'sin límite'}
                        </td>
                        <td className="px-5 py-3.5 hidden sm:table-cell">
                          <div className="flex flex-col gap-1">
                            {d.isGlobal ? (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-800 w-fit">Global</span>
                            ) : (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-800 w-fit">
                                {d.productIds.length} referencia(s)
                              </span>
                            )}
                            <span className="text-[10px] text-foreground-500">{discountLineLabel(d.lineType)}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${live ? 'bg-emerald-100 text-emerald-700' : d.isActive ? 'bg-amber-100 text-amber-700' : 'bg-background-100 text-foreground-500'}`}>
                            {live ? 'Vigente' : d.isActive ? 'Fuera de fecha' : 'Inactiva'}
                          </span>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleToggle(d)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:bg-background-100 transition-colors cursor-pointer"
                              title={d.isActive ? 'Desactivar' : 'Activar'}
                              aria-label="Activar o desactivar"
                            >
                              <i className={d.isActive ? 'ri-toggle-fill text-accent-500' : 'ri-toggle-line'}></i>
                            </button>
                            <button
                              onClick={() => {
                                setEditing({
                                  id: d.id,
                                  name: d.name,
                                  discountPercent: d.discountPercent,
                                  startDate: toLocalInput(d.startDate),
                                  endDate: toLocalInput(d.endDate),
                                  isGlobal: d.isGlobal,
                                  productIds: d.productIds,
                                  lineType: d.lineType,
                                  isActive: d.isActive,
                                });
                                setFormError('');
                              }}
                              className="w-8 h-8 flex items-center justify-center rounded-lg text-accent-600 hover:bg-accent-100 transition-colors cursor-pointer"
                              aria-label="Editar"
                            >
                              <i className="ri-edit-line"></i>
                            </button>
                            <button
                              onClick={() => handleDelete(d.id)}
                              className="w-8 h-8 flex items-center justify-center rounded-lg text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                              aria-label="Eliminar"
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {editing && (
        <Modal
          title={editing.id ? 'Editar promoción' : 'Nueva promoción'}
          subtitle="Descuento por porcentaje, fechas y referencias."
          onClose={() => setEditing(null)}
          maxWidth="max-w-xl"
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            {formError && (
              <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
                <i className="ri-error-warning-line"></i>{formError}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <label className={labelCls}>Nombre de la promoción *</label>
                <input
                  className={inputCls}
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  placeholder="Ej: Semana de la Patyfamilia"
                />
              </div>
              <div>
                <label className={labelCls}>Descuento (%) *</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  className={inputCls}
                  value={editing.discountPercent}
                  onChange={(e) => setEditing({ ...editing, discountPercent: Number(e.target.value) })}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Fecha de inicio</label>
                <input
                  type="datetime-local"
                  className={inputCls}
                  value={editing.startDate}
                  onChange={(e) => setEditing({ ...editing, startDate: e.target.value })}
                />
              </div>
              <div>
                <label className={labelCls}>Fecha de fin</label>
                <input
                  type="datetime-local"
                  className={inputCls}
                  value={editing.endDate}
                  onChange={(e) => setEditing({ ...editing, endDate: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className={labelCls}>Aplica a la línea</label>
              <div className="grid grid-cols-3 gap-2">
                {DISCOUNT_LINE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setEditing({ ...editing, lineType: opt.value })}
                    className={`px-2 py-2.5 rounded-lg border text-xs sm:text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      editing.lineType === opt.value
                        ? 'border-accent-500 bg-accent-50 text-accent-700'
                        : 'border-background-300 bg-white text-foreground-600'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-foreground-400 mt-1.5">
                El descuento solo se aplicará a los productos de la línea elegida.
              </p>
            </div>

            <div>
              <label className={labelCls}>Tipo de promoción</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setEditing({ ...editing, isGlobal: true })}
                  className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors cursor-pointer ${
                    editing.isGlobal ? 'border-accent-500 bg-accent-50 text-accent-700' : 'border-background-300 bg-white text-foreground-600'
                  }`}
                >
                  Global (todo el catálogo)
                </button>
                <button
                  type="button"
                  onClick={() => setEditing({ ...editing, isGlobal: false })}
                  className={`px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors cursor-pointer ${
                    !editing.isGlobal ? 'border-accent-500 bg-accent-50 text-accent-700' : 'border-background-300 bg-white text-foreground-600'
                  }`}
                >
                  Referencias específicas
                </button>
              </div>
            </div>

            {!editing.isGlobal && (
              <div>
                <label className={labelCls}>Referencias incluidas</label>
                <input
                  className={`${inputCls} mb-2`}
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  placeholder="Buscar por nombre o SKU..."
                />
                <div className="max-h-52 overflow-y-auto border border-background-200 rounded-lg divide-y divide-background-100">
                  {filteredProducts.length === 0 ? (
                    <p className="px-3 py-4 text-sm text-foreground-500">Sin resultados.</p>
                  ) : (
                    filteredProducts.map((p) => {
                      const checked = editing.productIds.includes(p.id);
                      return (
                        <label key={p.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-background-50">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              setEditing({
                                ...editing,
                                productIds: e.target.checked
                                  ? [...editing.productIds, p.id]
                                  : editing.productIds.filter((id) => id !== p.id),
                              });
                            }}
                            className="w-4 h-4 accent-accent-500 cursor-pointer"
                          />
                          <span className="text-sm text-foreground-800 flex-1 min-w-0 truncate">{p.name}</span>
                          <span className="text-xs text-foreground-400 whitespace-nowrap">{p.sku}</span>
                        </label>
                      );
                    })
                  )}
                </div>
                <p className="text-xs text-foreground-400 mt-1">{editing.productIds.length} referencia(s) seleccionada(s).</p>
              </div>
            )}

            <label className="flex items-center gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={editing.isActive}
                onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })}
                className="w-4 h-4 accent-accent-500 cursor-pointer"
              />
              <span className="text-sm text-foreground-700">
                {editing.isActive ? 'Activa' : 'Inactiva'}
              </span>
            </label>

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 px-4 py-2.5 text-sm font-semibold bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60"
              >
                {saving ? 'Guardando...' : 'Guardar promoción'}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </AdminLayout>
  );
}